import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import NavBar from '../ui/tubelight-navbar';
import { Home, Users, Info, Briefcase, Mail } from 'lucide-react';

const Navigation = () => {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('home');
  const [activeWorker, setActiveWorker] = useState(null);
  const isInitialMount = useRef(true);
  const skipNextScrollUpdate = useRef(false);

  const handleNavClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      skipNextScrollUpdate.current = true;
      // Only scroll if we're changing sections
      if (id !== activeSection) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setActiveSection(id);

      // Handle URL updates
      if (id === 'digitalWorkers') {
        // If we already have an active worker, maintain their URL
        if (activeWorker) {
          window.history.pushState(null, '', `/digital-workers/${activeWorker}`);
        } else {
          window.history.pushState(null, '', '/digital-workers');
        }
      } else {
        // For other sections
        const path = id === 'home' ? '/' : `/${id.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        window.history.pushState(null, '', path);
        setActiveWorker(null); // Clear active worker when navigating away
      }
    } else if (id === 'home') {
      skipNextScrollUpdate.current = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('home');
      setActiveWorker(null);
      window.history.pushState(null, '', '/');
    }
  };

  // Handle initial navigation and URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const path = window.location.pathname;
      
      // Special routes for standalone pages
      if (path === '/social-card') {
        return; // Let the standalone page render
      }
      if (path === '/mentor') {
        return; // Let the standalone page render
      }

      const match = path.match(/^\/digital-workers(?:\/([^/]+))?$/);
      
      if (match) {
        skipNextScrollUpdate.current = true;
        if (match[1]) {
          setActiveWorker(match[1]);
        }
        const element = document.getElementById('digitalWorkers');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection('digitalWorkers');
        }
        return;
      }
      
      // Map paths to section IDs
      const pathToSection = {
        '/': 'home',
        '/digital-workers': 'digitalWorkers',
        '/in-actions': 'in-actions',
        '/about-us': 'about-us',
        '/contact-us': 'contact-us'
      };

      const section = pathToSection[path];
      if (section) {
        skipNextScrollUpdate.current = true;
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(section);
          if (section !== 'digitalWorkers') {
            setActiveWorker(null);
          }
        }
      }
    };

    // Only handle URL on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      handleUrlChange();
    }

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (skipNextScrollUpdate.current) {
        skipNextScrollUpdate.current = false;
        return;
      }

      const sections = ['home', 'digitalWorkers', 'in-actions', 'about-us', 'contact-us'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      // Special handling for home section
      if (scrollPosition < window.innerHeight) {
        setActiveSection('home');
        if (window.location.pathname !== '/') {
          window.history.replaceState(null, '', '/');
        }
        return;
      }

      // Find the current section
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const sectionTop = window.scrollY + rect.top;
          const sectionBottom = sectionTop + rect.height;

          if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
            setActiveSection(section);
            
            if (section === 'digitalWorkers') {
              // Find all employee sections
              const employeeElements = document.querySelectorAll('[data-employee-id]');
              let visibleEmployee = null;
              let maxVisibleHeight = 0;

              employeeElements.forEach(empElement => {
                const empRect = empElement.getBoundingClientRect();
                // Calculate how much of the element is visible in the viewport
                const visibleTop = Math.max(0, empRect.top);
                const visibleBottom = Math.min(window.innerHeight, empRect.bottom);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);

                if (visibleHeight > maxVisibleHeight) {
                  maxVisibleHeight = visibleHeight;
                  visibleEmployee = empElement.getAttribute('data-employee-id');
                }
              });

              if (visibleEmployee && maxVisibleHeight > window.innerHeight * 0.3) {
                // Only update if employee section is significantly visible (30% of viewport)
                if (window.location.pathname !== `/digital-workers/${visibleEmployee}`) {
                  window.history.replaceState(null, '', `/digital-workers/${visibleEmployee}`);
                  setActiveWorker(visibleEmployee);
                }
              } else {
                // If no employee is significantly visible, use the default URL
                if (window.location.pathname !== '/digital-workers') {
                  window.history.replaceState(null, '', '/digital-workers');
                  setActiveWorker(null);
                }
              }
            } else {
              const path = section === 'home' ? '/' : `/${section.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
              if (window.location.pathname !== path) {
                window.history.replaceState(null, '', path);
              }
            }
            break;
          }
        }
      }
    };

    const throttledScroll = () => {
      if (!window.requestAnimationFrame) {
        setTimeout(handleScroll, 16);
      } else {
        window.requestAnimationFrame(handleScroll);
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  const navItems = [
    { 
      name: t('nav.menu.home'),
      url: '/',
      icon: Home,
      onClick: () => handleNavClick('home'),
      section: 'home',
      isActive: activeSection === 'home'
    },
    { 
      name: t('nav.menu.digitalWorkers'),
      url: activeWorker ? `/digital-workers/${activeWorker}` : '/digital-workers',
      icon: Users,
      onClick: () => handleNavClick('digitalWorkers'),
      section: 'digitalWorkers',
      isActive: activeSection === 'digitalWorkers'
    },
    { 
      name: t('nav.menu.projects'),
      url: '/in-actions',
      icon: Briefcase,
      onClick: () => handleNavClick('in-actions'),
      section: 'in-actions',
      isActive: activeSection === 'in-actions'
    },
    { 
      name: t('nav.menu.aboutUs'),
      url: '/about-us',
      icon: Info,
      onClick: () => handleNavClick('about-us'),
      section: 'about-us',
      isActive: activeSection === 'about-us'
    },
    { 
      name: t('nav.menu.contactUs'),
      url: '/contact-us',
      icon: Mail,
      onClick: () => handleNavClick('contact-us'),
      section: 'contact-us',
      isActive: activeSection === 'contact-us'
    }
  ];

  // Only show navigation on main page, not on standalone pages like mentor
  if (window.location.pathname === '/mentor') {
    return null;
  }

  return (
    <div className="mobile-nav-container" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 0 }}>
      <NavBar 
        items={navItems} 
        activeSection={activeSection}
        enableScrollBehavior={activeSection === 'home'}
      />
    </div>
  );
};

export default Navigation; 